import { PlayerRef } from '@remotion/player';
import { useCallback, useEffect, useRef, useState } from 'react';
import { EditorStarterItem } from '../items/item-type';
import { useIsPlaying } from '../playback-controls/use-is-playing';
import { calculateFrame } from '../timeline/utils/calculate-frame';
import { TimelineTrackAndLayout } from '../timeline/utils/drag/calculate-track-heights';
import {
  getItemAtFrameInTrack,
  getTrackIndexFromY,
} from './track-position-utils';
import { useSkimmingEnabled } from './use-context';

interface UseTimelineSkimmingProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  playerRef: React.RefObject<PlayerRef | null>;
  totalDurationInFrames: number;
  timelineWidth: number | null;
  tracks: TimelineTrackAndLayout[];
  items: Record<string, EditorStarterItem>;
  isDragging?: boolean;
}

interface UseTimelineSkimmingResult {
  skimFrame: number | null;
  originalFrame: number | null;
  isSkimming: boolean;
  hoveredTrackIndex: number | null;
  hoveredItemId: string | null;
  onMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave: () => void;
  onPointerDownCapture: React.PointerEventHandler<HTMLDivElement>;
  onClick: React.MouseEventHandler<HTMLDivElement>;
}

export const useTimelineSkimming = ({
  containerRef,
  scrollContainerRef,
  playerRef,
  totalDurationInFrames,
  timelineWidth,
  tracks,
  items,
  isDragging = false,
}: UseTimelineSkimmingProps): UseTimelineSkimmingResult => {
  const isSkimmingEnabled = useSkimmingEnabled();
  const isPlaying = useIsPlaying(playerRef);
  const [skimFrame, setSkimFrame] = useState<number | null>(null);
  const [isSkimming, setIsSkimming] = useState(false);
  const [hoveredTrackIndex, setHoveredTrackIndex] = useState<number | null>(
    null,
  );
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
  const [originalFrame, setOriginalFrame] = useState<number | null>(null);
  const pendingClick = useRef<{
    pointerId: number;
    x: number;
    y: number;
    target: EventTarget;
  } | null>(null);

  const canSkim = isSkimmingEnabled && !isPlaying && !isDragging;

  const onMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (
        !canSkim || e.buttons !== 0 ||
        !containerRef.current || timelineWidth === null
      ) {
        return;
      }

      const frame = calculateFrame({
        container: containerRef.current,
        xCoordinate: e.clientX,
        totalDurationInFrames,
        timelineWidth,
      });

      if (!isSkimming) {
        setOriginalFrame(playerRef.current?.getCurrentFrame() ?? 0);
        setIsSkimming(true);
      }

      setSkimFrame(frame);
      playerRef.current?.seekTo(frame);

      // 스크롤 컨테이너의 rect를 사용해야 함 (스크롤해도 위치가 변하지 않음)
      const scrollContainerRect =
        scrollContainerRef.current?.getBoundingClientRect();
      const scrollTop = scrollContainerRef.current?.scrollTop ?? 0;
      const trackIndex = scrollContainerRect
        ? getTrackIndexFromY({
            clientY: e.clientY,
            containerRect: scrollContainerRect,
            tracks,
            scrollTop,
          })
        : null;
      setHoveredTrackIndex(trackIndex);

      if (trackIndex !== null && tracks[trackIndex]) {
        const trackAndLayout = tracks[trackIndex];
        const itemId = getItemAtFrameInTrack({
          track: trackAndLayout.track,
          frame,
          items,
        });
        setHoveredItemId(itemId);
      } else {
        setHoveredItemId(null);
      }
    },
    [
      canSkim,
      containerRef,
      scrollContainerRef,
      timelineWidth,
      totalDurationInFrames,
      isSkimming,
      playerRef,
      tracks,
      items,
    ],
  );

  // Observe the gesture before clip pointerdown handlers stop propagation.
  // Capture only records intent; seeking waits for a completed click.
  const onPointerDownCapture = useCallback<React.PointerEventHandler<HTMLDivElement>>(
    (event) => {
      pendingClick.current = null;
      if (
        !canSkim || event.button !== 0 || event.metaKey ||
        event.ctrlKey || event.shiftKey || event.altKey
      ) return;
      if (!(event.target instanceof Element)) return;
      if (event.target.closest(
        'button, input, textarea, select, a, [role="button"], [role="menu"], [contenteditable="true"]',
      )) return;
      // Trim, fade, rolling-edit and volume handles own their pointer gestures.
      for (
        let target: Element | null = event.target;
        target && target !== event.currentTarget;
        target = target.parentElement
      ) {
        if (/cursor-\S*resize/.test(target.getAttribute('class') ?? '')) return;
        if (target instanceof HTMLElement && target.style.cursor.includes('resize')) return;
      }
      pendingClick.current = {
        pointerId: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        target: event.target,
      };
    },
    [canSkim],
  );

  useEffect(() => {
    const trackMovement = (event: PointerEvent) => {
      const pending = pendingClick.current;
      if (
        pending && pending.pointerId === event.pointerId &&
        Math.hypot(event.clientX - pending.x, event.clientY - pending.y) >= 3
      ) {
        pendingClick.current = null;
      }
    };
    const cancel = () => {
      pendingClick.current = null;
    };
    window.addEventListener('pointermove', trackMovement, true);
    window.addEventListener('pointerup', trackMovement, true);
    window.addEventListener('pointercancel', cancel, true);
    window.addEventListener('blur', cancel);
    return () => {
      window.removeEventListener('pointermove', trackMovement, true);
      window.removeEventListener('pointerup', trackMovement, true);
      window.removeEventListener('pointercancel', cancel, true);
      window.removeEventListener('blur', cancel);
    };
  }, []);

  const onClick = useCallback<React.MouseEventHandler<HTMLDivElement>>(
    (event) => {
      const pending = pendingClick.current;
      pendingClick.current = null;
      if (
        !pending || !canSkim || event.defaultPrevented || event.button !== 0 ||
        event.metaKey || event.ctrlKey || event.shiftKey || event.altKey ||
        event.target !== pending.target || !containerRef.current || timelineWidth === null
      ) return;
      const frame = calculateFrame({
        container: containerRef.current,
        xCoordinate: event.clientX,
        totalDurationInFrames,
        timelineWidth,
      });
      setOriginalFrame(frame);
      setSkimFrame(frame);
      setIsSkimming(true);
      playerRef.current?.seekTo(frame);
    },
    [canSkim, containerRef, timelineWidth, totalDurationInFrames, playerRef],
  );

  const onMouseLeave = useCallback(() => {
    if (!isSkimmingEnabled) {
      return;
    }

    // skimmer는 마지막 위치에 유지, hover 관련 상태만 초기화
    setHoveredTrackIndex(null);
    setHoveredItemId(null);
    // isSkimming, skimFrame은 유지하여 skimmer가 사라지지 않도록 함
  }, [isSkimmingEnabled]);

  useEffect(() => {
    if (!canSkim) pendingClick.current = null;
    if (!canSkim && isSkimming) {
      setIsSkimming(false);
      setSkimFrame(null);
      setHoveredTrackIndex(null);
      setHoveredItemId(null);
      setOriginalFrame(null);
    }
  }, [canSkim, isSkimming]);

  return {
    skimFrame,
    originalFrame,
    isSkimming,
    hoveredTrackIndex,
    hoveredItemId,
    onMouseMove,
    onMouseLeave,
    onPointerDownCapture,
    onClick,
  };
};
